import React, { useEffect, useState } from 'react';
import API from '../../services/api';
import styles from '../../styles/MenuPanel.module.css';

export default function MenuPanel() {

    const [menu, setMenu] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editingItem, setEditingItem] = useState(null);
    const [deletingItem, setDeletingItem] = useState(null);
    const [createForm, setCreateForm] = useState({
    id: '',
    category_id: '',
    name: '',
    price: '',
    description: '',
    image: '',
    tags: ''
});

    /* ---------------------------
        FETCH MENU
    ---------------------------- */
    const fetchMenu = async () => {
        try {
            setLoading(true);
            setError('');

            const res = await API.get('/menu');
            setMenu(res.data);

        } catch (err) {
            console.error(err);
            setError('Failed to load menu');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMenu();
    }, []);

    /* ---------------------------
        UPDATE ITEM (INLINE EDIT)
    ---------------------------- */
    const handleUpdate = async (item) => {
        try {

            await API.patch(`/menu/item/${item.id}`, {
                name: item.name,
                price: item.price,
                description: item.description
            });

            setEditingItem(null);
            fetchMenu();

        } catch (err) {
            console.error(err);
            setError('Failed to update item');
        }
    };

    const createItem = async () => {

    if (!createForm.id || !createForm.category_id || !createForm.name) {
        setError("ID, category and name are required");
        return;
    }

    try {

        await API.post('/menu/item', {
            id: createForm.id,
            category_id: createForm.category_id,
            name: createForm.name,
            price: Number(createForm.price),
            description: createForm.description,
            image: createForm.image,
            tags: createForm.tags
                ? createForm.tags.split(',').map(t => t.trim())
                : []
        });

        setCreateForm({
            id: '',
            category_id: '',
            name: '',
            price: '',
            description: '',
            image: '',
            tags: ''
        });

        fetchMenu();

    } catch (err) {
        console.error(err);
        setError("Failed to create item");
    }
};

const DeleteItem = async(item) => {
    try{
        await API.delete(`/menu/item/${item.id}`, {
            id: item.id
        });

        fetchMenu();
    } catch (err){
        console.error(err);
        setError("Failed to delete item")
    }
};

const trueUrl = (url) => {
  if (!url) return url;

  try {
    const u = new URL(url);

    u.pathname = u.pathname.replace(/^\/api/, '');

    return u.origin + u.pathname;
  } catch (e) {
    // fallback for relative URLs like "/api/uploads/menu"
    return url.replace(/^\/api/, '');
  }
};

/* ---------------------------
    IMAGE UPLOAD
---------------------------- */
const handleImageUpload = async (itemId, file) => {

    if (!file) return;

    try {

        const formData = new FormData();
        formData.append('image', file);

        await API.post(
            `/menu/upload/${itemId}`,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            }
        );

        fetchMenu();

    } catch (err) {

        console.error(err);
        setError('Image upload failed');

    }
};

    /* ---------------------------
        LOADING
    ---------------------------- */
    if (loading) {
        return <h2>Loading menu...</h2>;
    }

    if (error) {
        return <p>{error}</p>;
    }

    return (
        <div className={styles.menuPanel}>

            <h2>Menu Management</h2>

            {/* CREATE NEW ITEM FORM */}
            <div className={styles.createBox}>

                <h3>Create New Item</h3>

                <input
                    placeholder="ID (e.g. greek-salad)"
                    value={createForm.id}
                    onChange={(e) =>
                        setCreateForm({ ...createForm, id: e.target.value })
                    }
                />

                <select
                    value={createForm.category_id}
                    onChange={(e) =>
                        setCreateForm({ ...createForm, category_id: e.target.value })
                    }
                >
                    <option value="">Select category</option>

                    {menu.categories.map(cat => (
                        <option key={cat.id} value={cat.id}>
                            {cat.name}
                        </option>
                    ))}
                </select>

                <input
                    placeholder="Name"
                    value={createForm.name}
                    onChange={(e) =>
                        setCreateForm({ ...createForm, name: e.target.value })
                    }
                />

                <input
                    type="number"
                    placeholder="Price"
                    value={createForm.price}
                    onChange={(e) =>
                        setCreateForm({ ...createForm, price: e.target.value })
                    }
                />

                <input
                    placeholder="Description"
                    value={createForm.description}
                    onChange={(e) =>
                        setCreateForm({ ...createForm, description: e.target.value })
                    }
                />

                <input
                    placeholder="Image URL"
                    value={createForm.image}
                    onChange={(e) =>
                        setCreateForm({ ...createForm, image: e.target.value })
                    }
                />

                <input
                    placeholder="Tags (comma separated)"
                    value={createForm.tags}
                    onChange={(e) =>
                        setCreateForm({ ...createForm, tags: e.target.value })
                    }
                />

                <button onClick={createItem}>
                    Add Item
                </button>

            </div>

            {menu.categories.map((cat) => (

                <div key={cat.id} className={styles.category}>

                    <h3>{cat.name}</h3>

                    {cat.items.map((item) => {

                        const isEditing = editingItem === item.id;

                        return (
                            <div key={item.id} className={styles.itemRow}>

                                {/* IMAGE */}

                                <div className={styles.imageBox}>

                                    {item.image ? (

                                        <img
                                            src={trueUrl(item.image)}
                                            alt={item.name}
                                            className={styles.imagePreview}
                                        />

                                    ) : (

                                        <div className={styles.noImage}>
                                            No image
                                        </div>

                                    )}

                                    <label className={styles.uploadButton}>
                                        Upload
                                        <input
                                            type="file"
                                            accept="image/*"
                                            hidden
                                            onChange={(e) =>
                                                handleImageUpload(
                                                    item.id,
                                                    e.target.files[0]
                                                )
                                            }
                                        />
                                    </label>
                                </div>

                                {/* NAME */}
                                {isEditing ? (
                                    <input
                                        value={item.name}
                                        onChange={(e) => {
                                            const updated = { ...item, name: e.target.value };
                                            setMenu(prev => ({
                                                ...prev,
                                                categories: prev.categories.map(c => ({
                                                    ...c,
                                                    items: c.items.map(i =>
                                                        i.id === item.id ? updated : i
                                                    )
                                                }))
                                            }));
                                        }}
                                    />
                                ) : (
                                    <span>{item.name}</span>
                                )}

                                {/* DESCRIPTION */}
                                {isEditing ? (
                                    <input
                                        value={item.description}
                                        onChange={(e) => {
                                            const updated = { ...item, name: e.target.value };
                                            setMenu(prev => ({
                                                ...prev,
                                                categories: prev.categories.map(c => ({
                                                    ...c,
                                                    items: c.items.map(i =>
                                                        i.id === item.id ? updated : i
                                                    )
                                                }))
                                            }));
                                        }}
                                    />
                                ) : (
                                    <span>{item.description}</span>
                                )}

                                {/* PRICE */}
                                {isEditing ? (
                                    <input
                                        type="number"
                                        value={item.price || 0}
                                        onChange={(e) => {
                                            const updated = { ...item, price: parseFloat(e.target.value) };
                                            setMenu(prev => ({
                                                ...prev,
                                                categories: prev.categories.map(c => ({
                                                    ...c,
                                                    items: c.items.map(i =>
                                                        i.id === item.id ? updated : i
                                                    )
                                                }))
                                            }));
                                        }}
                                    />
                                ) : (
                                    <span>€{item.price?.toFixed(2) || '—'}</span>
                                )}

                                {/* IMAGE ACTIONS (only when editing) */}
                                {isEditing && item.image && (
                                    <button
                                        onClick={async () => {
                                            try {
                                                await API.delete(`/menu/item/${item.id}/image`);
                                                fetchMenu();
                                            } catch (err) {
                                                console.error(err);
                                                setError("Failed to delete image");
                                            }
                                        }}
                                        style={{
                                            background: "#b33",
                                            color: "white",
                                            border: "none",
                                            padding: "6px 10px",
                                            borderRadius: "6px",
                                            cursor: "pointer",
                                            marginTop: "6px"
                                        }}
                                    >
                                        Remove Image
                                    </button>
                                )}

                                {/* ACTIONS */}
                                {isEditing ? (
                                    <>
                                        <button onClick={() => handleUpdate(item)}>
                                            Save
                                        </button>

                                        <button onClick={() => setEditingItem(null)}>
                                            Cancel
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button onClick={() => setEditingItem(item.id)}>
                                            Edit
                                        </button>

                                        <button onClick={() => DeleteItem(item)}>
                                            Delete
                                        </button>
                                    </>
                                )}

                            </div>
                        );
                    })}

                </div>

            ))}

        </div>
    );
}